import useMedia from "utils/hooks/useMedia.ts";
import { MOBILE_VIRTUAL_LIST } from "common/constants.ts";
import { useTheme } from "styled-components";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { sharedLib } from "@shared/services/shared.service.ts";
import { MouseEvent, useContext, useMemo, useState } from "react";
import { formatTimeSeriesChartData } from "@shared/utils/charts.ts";
import { getPublicBotEditPath, getPublicBotPath } from "utils/routes.tsx";
import { BotTemplateData, BotType } from "@shared/types";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context.ts";

interface usePublicBotCardProps {
  botData: BotTemplateData;
  onBotClick?: () => void;
  prepareAccountOnClick?: boolean;
  botRank?: number;
  isBotForm?: boolean;
}

const usePublicBotCard = ({
  botData,
  onBotClick,
  prepareAccountOnClick,
  botRank,
  isBotForm,
}: usePublicBotCardProps) => {
  const isMobile = useMedia(
    [`(max-width: ${MOBILE_VIRTUAL_LIST})`],
    [true],
    false
  );
  const appTheme = useTheme();
  const history = useHistory();
  const user = useSelector(sharedLib.selectors.authentication.selectUser);
  const showNoExchangeModal =
    !(prepareAccountOnClick && onBotClick) &&
    !user?.has_valid_clients_connected;
  const { setShouldInitialSymbolAndAccount } = useContext(BotFormContext) || {};

  const [noExchangeModalVisible, setNoExchangeModalVisible] = useState(false);
  const [publicBotDrawerOpen, setPublicBotDrawerOpen] = useState(false);

  const formattedChartData = useMemo(
    () =>
      formatTimeSeriesChartData(
        botData.graph_data.accumulated_profit,
        1,
        false,
        null,
        8,
        true
      ),
    [botData.graph_data.accumulated_profit]
  );

  const openPublicBotPreviewPage = () => {
    history.push(getPublicBotPath(BotType.ASAP, botData.uuid, botRank, true));
  };

  const onBotCardClick = () => {
    if (showNoExchangeModal) {
      setNoExchangeModalVisible(true);
    } else {
      if (isBotForm) setPublicBotDrawerOpen(true);
      else {
        (onBotClick || openPublicBotPreviewPage)();
      }
    }
  };

  const navigateToPublicBotForm = () => {
    history.push(getPublicBotEditPath(BotType.ASAP, botData?.uuid));
  };

  const onCopyClick = (e: MouseEvent) => {
    if (isBotForm) {
      e.stopPropagation();
      setShouldInitialSymbolAndAccount?.(true);
      navigateToPublicBotForm();
    }
  };

  return {
    appTheme,
    isMobile,
    formattedChartData,
    onBotCardClick,
    onCopyClick,
    noExchangeModalVisible,
    setNoExchangeModalVisible,
    publicBotDrawerOpen,
    setPublicBotDrawerOpen,
    showNoExchangeModal,
  };
};

export default usePublicBotCard;
